import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogIn, Loader2, Cloud, Zap, Tv } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '@/lib/utils';
import { callbackDestination } from '@/lib/landing';
import type { LoginState } from '@/components/auth/RequireAuth';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, isAuthenticated } = useAuth();

  const [isLoading, setIsLoading] = useState(false);

  // Where RequireAuth was asked to go before it sent the user here, if anywhere.
  const rawFrom = (location.state as LoginState | null)?.from;
  const from = typeof rawFrom === 'string' ? rawFrom : undefined;

  // Redirect if already authenticated. callbackDestination keeps it on this
  // origin and sends `/` (or nothing) to the role landing.
  useEffect(() => {
    if (isAuthenticated) {
      navigate(callbackDestination(from), { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const handleSignIn = async () => {
    setIsLoading(true);
    try {
      await login(from);
    } catch {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-deep flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-surface-deep via-surface to-surface-deep" />

        {/* Animated background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full">
            {Array.from({ length: 20 }).map((_, i) => (
              <div
                key={i}
                className="absolute rounded-full bg-gold/20"
                style={{
                  width: Math.random() * 200 + 50,
                  height: Math.random() * 200 + 50,
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  filter: 'blur(40px)',
                }}
              />
            ))}
          </div>
        </div>

        {/* Gold accent line */}
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-gold to-transparent" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center items-center p-12 w-full">
          {/* Logo */}
          <div className="mb-8">
            <img src="/logo.png" alt="Jeket" className="h-20" />
          </div>
          <p className="text-xl text-silver-muted tracking-wide mb-8">
            Streaming Service
          </p>

          {/* Feature highlights */}
          <div className="space-y-4 max-w-sm">
            <div className="flex items-center gap-4 text-silver-muted">
              <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center">
                <Cloud size={20} className="text-gold" />
              </div>
              <div>
                <div className="text-sm font-medium text-silver-bright">Live Streaming</div>
                <div className="text-xs">Cloud-native stream delivery</div>
              </div>
            </div>
            <div className="flex items-center gap-4 text-silver-muted">
              <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center">
                <Zap size={20} className="text-gold" />
              </div>
              <div>
                <div className="text-sm font-medium text-silver-bright">Ad Insertion</div>
                <div className="text-xs">Dynamic SCTE-35 ad breaks</div>
              </div>
            </div>
            <div className="flex items-center gap-4 text-silver-muted">
              <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center">
                <Tv size={20} className="text-gold" />
              </div>
              <div>
                <div className="text-sm font-medium text-silver-bright">Multi-Channel</div>
                <div className="text-xs">Scalable channel management</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <img src="/logo.png" alt="Jeket" className="h-12 mx-auto mb-2" />
            <p className="text-sm text-silver-muted mt-1">Streaming Service</p>
          </div>

          {/* Login card */}
          <div className="bg-surface border border-gold/20 rounded-2xl p-8 shadow-jeket">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-semibold text-silver-bright">
                Welcome Back
              </h2>
              <p className="text-sm text-silver-muted mt-2">
                Sign in to access your dashboard
              </p>
            </div>

            {/* SSO Sign In Button */}
            <button
              onClick={handleSignIn}
              disabled={isLoading}
              className={cn(
                'w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-semibold transition-all',
                isLoading
                  ? 'bg-gold/50 text-surface-deep cursor-not-allowed'
                  : 'bg-gold hover:bg-gold-light text-surface-deep hover:shadow-jeket-gold'
              )}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Redirecting...
                </>
              ) : (
                <>
                  <LogIn className="h-5 w-5" />
                  Sign in with Jeket
                </>
              )}
            </button>

            <p className="text-center text-sm text-silver-muted mt-4">
              Don't have an account?{' '}
              <button
                onClick={register}
                className="text-gold hover:text-gold-light font-medium transition-colors"
              >
                Sign up
              </button>
            </p>
          </div>

          {/* Footer */}
          <p className="text-center text-xs text-silver-muted mt-8">
            Jeket Streaming Service v0.1.0
          </p>
        </div>
      </div>
    </div>
  );
}
